import { signal, computed, Signal } from '@angular/core';

export type StoreStatus = 'idle' | 'loading' | 'success' | 'error';

export interface StoreState<T> {
  records: T[];
  status: StoreStatus;
  error: string | null;
}

export abstract class AbstractStore<T extends { id: string | number }> {
  // --- Core signals ---
  private readonly _records = signal<T[]>([]);
  private readonly _status = signal<StoreStatus>('idle');
  private readonly _error = signal<string | null>(null);

  // --- Public readonly signals ---
  readonly records: Signal<T[]> = this._records.asReadonly();
  readonly status: Signal<StoreStatus> = this._status.asReadonly();
  readonly error: Signal<string | null> = this._error.asReadonly();

  // --- Computed state signals ---
  readonly isLoading = computed(() => this._status() === 'loading');
  readonly hasError = computed(() => this._status() === 'error');
  readonly isEmpty = computed(
    () => this._records().length === 0 && this._status() === 'success',
  );
  readonly count = computed(() => this._records().length);

  // --- State transitions ---

  /**
   * Set the store's state to loading
   */
  public setLoading() {
    this._status.set('loading');
    this._error.set(null);
  }

  /**
   * Give the store an error state higher components can read
   * @param message Error message to set to
   */
  public setError(message: string) {
    this._status.set('error');
    this._error.set(message);
  }

  /**
   * Give the store records to hold
   * @param records Records to set to
   */
  public setRecords(records: T[]): void {
    this._records.set(records);
    this._status.set('success');
    this._error.set(null);
  }

  /**
   * Reset the store to its initial idle state
   */
  public reset(): void {
    this._records.set([]);
    this._status.set('idle');
    this._error.set(null);
  }

  // --- Optimistic helpers subclasses can call ---

  /**
   * Add a record to the store
   * @param record Record to add
   */
  public addRecord(record: T) {
    this._records.update((current) => [...current, record]);
  }

  /**
   * Update a record in the store
   * @param id Id field to update
   * @param changes Updated patch of changes
   */
  protected patchRecord(id: T['id'], changes: Partial<T> | ((prev: T) => T)) {
    this._records.update((current) =>
      current.map((r) => {
        if (r.id !== id) return r;

        if (typeof changes === 'function') {
          // Use updater function to produce new instance
          return (changes as (prev: T) => T)(r);
        }

        // Assume object patch
        return { ...r, ...changes } as T;
      }),
    );
  }

  /**
   * Remove a record in the store
   * @param id Record id to remove
   */
  public removeRecord(id: T['id']) {
    this._records.update((current) => current.filter((r) => r.id !== id));
  }

  // --- Snapshot for rollback support ---

  /**
   * Capture current state, e.g. before an optimistic mutation
   * @return store's current state
   */
  public snapshot(): StoreState<T> {
    return {
      records: this._records(),
      status: this._status(),
      error: this._error(),
    };
  }

  /**
   * Restore a previously captured snapshot, e.g. on sync failure
   * @param snapshot State of the store to reset to
   */
  public restore(snapshot: StoreState<T>): void {
    this._records.set(snapshot.records);
    this._status.set(snapshot.status);
    this._error.set(snapshot.error);
  }
}
